// Settings persistence utilities

import { DEFAULT_CUSTOM_COLORS, DEFAULT_GAME_SOUNDS_ENABLED } from './constants';

const STORAGE_KEYS = {
  THEME: 'migoyugoTheme',
  SOUND_ENABLED: 'migoyugoSoundEnabled',
  GAME_SOUNDS_ENABLED: 'migoyugoGameSoundsEnabled',
  CUSTOM_COLORS: 'migoyugoCustomColors',
  PWA_BANNER_DISMISSED: 'pwa-banner-dismissed',
  AUTH_TOKEN: 'authToken'
} as const;

export interface CustomColors {
  whiteMigo: string;
  blackMigo: string;
  yugoColor: string;
  whiteYugo: string;
  blackYugo: string;
  boardColor: string;
  hoverColor: string;
  lastMoveColor: string;
}

/**
 * Loads saved settings from localStorage
 * Prefers migoyugoGameSoundsEnabled (main app key) over migoyugoSoundEnabled (legacy)
 */
export const loadSavedSettings = (): {
  soundEnabled: boolean;
  customColors: CustomColors;
} => {
  const savedGameSounds = localStorage.getItem(STORAGE_KEYS.GAME_SOUNDS_ENABLED);
  const savedSoundEnabled = localStorage.getItem(STORAGE_KEYS.SOUND_ENABLED);
  const savedCustomColors = localStorage.getItem(STORAGE_KEYS.CUSTOM_COLORS);

  // Prefer gameSoundsEnabled (main app), fallback to legacy soundEnabled
  const soundEnabled = savedGameSounds !== null
    ? JSON.parse(savedGameSounds)
    : savedSoundEnabled !== null
      ? JSON.parse(savedSoundEnabled)
      : DEFAULT_GAME_SOUNDS_ENABLED;

  return {
    soundEnabled,
    customColors: savedCustomColors ? JSON.parse(savedCustomColors) : DEFAULT_CUSTOM_COLORS
  };
};

/**
 * Saves theme to localStorage
 */
export const saveTheme = (theme: string): void => {
  localStorage.setItem(STORAGE_KEYS.THEME, theme);
};

/**
 * Saves sound enabled state to localStorage
 */
export const saveSoundEnabled = (enabled: boolean): void => {
  localStorage.setItem(STORAGE_KEYS.SOUND_ENABLED, JSON.stringify(enabled));
};

/**
 * Saves custom colors to localStorage
 */
export const saveCustomColors = (colors: CustomColors): void => {
  localStorage.setItem(STORAGE_KEYS.CUSTOM_COLORS, JSON.stringify(colors));
};

/**
 * Removes all settings from localStorage
 */
export const clearAllSettings = (): void => {
  localStorage.removeItem(STORAGE_KEYS.THEME);
  localStorage.removeItem(STORAGE_KEYS.SOUND_ENABLED);
  localStorage.removeItem(STORAGE_KEYS.CUSTOM_COLORS);
};

/**
 * Checks if PWA banner was dismissed
 */
export const isPWABannerDismissed = (): boolean => {
  return !!localStorage.getItem(STORAGE_KEYS.PWA_BANNER_DISMISSED);
};

/**
 * Marks PWA banner as dismissed
 */
export const dismissPWABanner = (): void => {
  localStorage.setItem(STORAGE_KEYS.PWA_BANNER_DISMISSED, Date.now().toString());
};

/**
 * Gets auth token from localStorage
 */
export const getAuthToken = (): string | null => {
  return localStorage.getItem(STORAGE_KEYS.AUTH_TOKEN);
};

/**
 * Sets auth token in localStorage
 */
export const setAuthToken = (token: string): void => {
  localStorage.setItem(STORAGE_KEYS.AUTH_TOKEN, token);
};

/**
 * Removes auth token from localStorage
 */
export const removeAuthToken = (): void => {
  localStorage.removeItem(STORAGE_KEYS.AUTH_TOKEN);
};

